import { useState } from 'react'
import { useAtom } from 'jotai'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { logSentryError } from '@node-space/utils'
import { ERROR_STATUS_CODES } from 'constants/General'
import { useCreateContactBankAccountMutation } from 'hooks/mutations/Contacts/useCreateContactBankAccount'
import { useCreateContactBankAccount2FAMutation } from 'hooks/mutations/Contacts/useCreateContactBankAccount2FAMutation'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { contactActionAtom } from 'pages/Contacts/atoms/contactAtoms'
import {
  ContactFiatInstrumentFormData,
  ContactFiatInstrumentFormKeys,
} from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { BaseErrorResponse, BeneficiaryTransferDestination } from 'types/beneficiaries'
import {
  AccountHolderName,
  Contact,
  ContactEntity,
  ContactPaymentInstrument,
  CreateContactBankAccountRequest,
  InstrumentType,
} from 'types/contacts'
import { TwoFAAuthRequest } from 'types/generics'

interface UseCreateContactBankAccountProps {
  contact?: Contact
  formValues?: ContactFiatInstrumentFormData
  form?: UseFormReturn<ContactFiatInstrumentFormData>
  onSubmit2FASuccess: (paymentInstrument: ContactPaymentInstrument) => void
}

export const useCreateContactBankAccount = ({
  contact,
  formValues,
  form,
  onSubmit2FASuccess,
}: UseCreateContactBankAccountProps) => {
  const { t } = useTranslation()
  const { twoFactorEnabled } = useTwoFAContext()
  const [contactsAction] = useAtom(contactActionAtom)

  const [calloutError, setCalloutError] = useState<string | null>(null)

  const {
    mutate: submitBankAccount,
    isPending: isSubmittingBankAccount,
    error: submitBankAccountError,
  } = useCreateContactBankAccountMutation()

  const { mutate: submit2FA, isPending: isSubmitting2FA } = useCreateContactBankAccount2FAMutation()

  const getRequestBankPayload = (formValues: ContactFiatInstrumentFormData) => {
    const {
      bankName,
      bankCode,
      routingNumber,
      bankAddressLine1,
      bankAddressLine2,
      bankCity,
      bankRegion,
      bankPostCode,
      bankCountryCode,
    } = formValues ?? {}

    return {
      ...(bankName && { name: bankName }),
      identificationCode: bankCode,
      ...(routingNumber && { nid: routingNumber }),
      address: {
        ...(bankAddressLine1 && { addressLine1: bankAddressLine1 }),
        ...(bankAddressLine2 && { addressLine2: bankAddressLine2 }),
        ...(bankRegion && { region: bankRegion }),
        ...(bankCity && { city: bankCity }),
        ...(bankPostCode && { postCode: bankPostCode }),
        ...(bankCountryCode && { country: bankCountryCode }),
      },
    }
  }

  const getRequestIntermediaryBankPayload = (formValues: ContactFiatInstrumentFormData) => {
    const {
      transferDestination,
      intermediaryBankCode,
      intermediaryBankName,
      intermediaryBankAddressLine1,
      intermediaryBankAddressLine2,
      intermediaryBankCity,
      intermediaryBankRegion,
      intermediaryBankPostCode,
      intermediaryBankCountryCode,
    } = formValues ?? {}
    const isSWIFT = transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
    const hasIntermediaryBank = isSWIFT && intermediaryBankCode

    if (!hasIntermediaryBank) return

    return [
      {
        bankName: intermediaryBankName,
        identificationCode: intermediaryBankCode,
        address: {
          ...(intermediaryBankAddressLine1 && { addressLine1: intermediaryBankAddressLine1 }),
          ...(intermediaryBankAddressLine2 && { addressLine2: intermediaryBankAddressLine2 }),
          ...(intermediaryBankCity && { city: intermediaryBankCity }),
          ...(intermediaryBankRegion && { region: intermediaryBankRegion }),
          ...(intermediaryBankPostCode && { postCode: intermediaryBankPostCode }),
          ...(intermediaryBankCountryCode && { country: intermediaryBankCountryCode }),
        },
      },
    ]
  }

  const getRequestPayload = (
    contact: Contact,
    formValues: ContactFiatInstrumentFormData
  ): CreateContactBankAccountRequest => {
    const isBusinessContact = contact?.entity?.type === ContactEntity.COMPANY
    const {
      currency,
      description,
      transferDestination,
      accountNumber,
      beneficiaryAddressLine1,
      beneficiaryAddressLine2,
      beneficiaryRegion,
      beneficiaryCity,
      beneficiaryCountryCode,
      beneficiaryPostCode,
    } = formValues ?? {}

    const bankDetails = getRequestBankPayload(formValues)
    const intermediaryBankDetails = getRequestIntermediaryBankPayload(formValues)
    const accountHolderName: AccountHolderName = isBusinessContact
      ? { legalName: formValues?.businessName }
      : {
          firstName: formValues?.firstName,
          lastName: formValues?.lastName,
        }

    const request: CreateContactBankAccountRequest = {
      type: InstrumentType.BANK_ACCOUNT,
      currency,
      description,
      destinations: [transferDestination as BeneficiaryTransferDestination],
      accountNumber,
      accountHolderName,
      accountHolderAddress: {
        ...(beneficiaryAddressLine1 && {
          addressLine1: beneficiaryAddressLine1,
        }),
        ...(beneficiaryAddressLine2 && {
          addressLine2: beneficiaryAddressLine2,
        }),
        ...(beneficiaryRegion && { region: beneficiaryRegion }),
        ...(beneficiaryCity && { city: beneficiaryCity }),
        ...(beneficiaryCountryCode && { country: beneficiaryCountryCode }),
        ...(beneficiaryPostCode && { postCode: beneficiaryPostCode }),
      },
      bank: {
        ...bankDetails,
      },
      ...(intermediaryBankDetails && { intermediaryBanks: intermediaryBankDetails }),
    }

    return request
  }

  const onSubmitBankAccount = () => {
    setCalloutError(null)
    form?.clearErrors()

    const requestPayload = getRequestPayload(contact, formValues)

    submitBankAccount(
      { contactId: contact?.id, request: requestPayload },
      {
        onError: onPreAuthErrorHandler,
      }
    )
  }

  const onPreAuthErrorHandler = (error: BaseErrorResponse) => {
    const { twoFactorCode } = formValues ?? {}
    const { challenge, callbackUrl } = error?.data ?? {}

    const isAuthRequired = error?.status === ERROR_STATUS_CODES?.AUTH_REQUIRED

    if (isAuthRequired) {
      onSubmit2FARequest({
        challenge: challenge?.value || '',
        tokenResponse: twoFactorCode || '',
        callbackUrl: callbackUrl || '',
      })
    } else if (error) {
      handleGenericError('onSubmitBankAccount', error)
    }
  }

  const onPostAuthErrorHandler = (error: BaseErrorResponse) => {
    const isAuthError = error?.status === ERROR_STATUS_CODES?.FORBIDDEN

    if (isAuthError) {
      form?.setError('twoFactorCode', {
        message: twoFactorEnabled ? t('authCodeInvalid') : t('authPasswordInvalid'),
      })
    } else if (error) {
      handleGenericError('onSubmit2FARequest', error)
    }
  }

  const handleGenericError = (errorTitle: string, error: BaseErrorResponse) => {
    if (error?.data?.errorList) {
      error.data.errorList.forEach(err => {
        form?.setError(err?.parameter as ContactFiatInstrumentFormKeys, { message: error?.message })
      })
    } else {
      setCalloutError(error?.data?.message ?? t('createBankAccountError'))
      logSentryError(`Error from ContactBankAccount - ${errorTitle}`, error, {
        contactId: contact?.id,
        currency: formValues?.currency,
        description: formValues?.description,
      })
    }
  }

  const onSubmit2FARequest = (payload: TwoFAAuthRequest) => {
    if (!payload?.callbackUrl) return

    submit2FA(payload, {
      onSuccess: (data: ContactPaymentInstrument) => {
        onSubmit2FASuccess?.(data)
        contactsAction?.onClose?.()
      },
      onError: onPostAuthErrorHandler,
    })
  }

  return {
    isSubmittingBankAccount,
    submitBankAccountError,
    isSubmitting2FA,
    onSubmitBankAccount,
    calloutError,
    isLoading: isSubmittingBankAccount || isSubmitting2FA,
  }
}
